import { OnDestroy, Injector, DestroyRef, Injectable, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { 
  Firestore} from '@angular/fire/firestore';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  collectionData, 
  docData
} from '@angular/fire/firestore';
import { Observable, BehaviorSubject, Subscription, EMPTY, from, throwError, of, switchMap, firstValueFrom } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { GroupMember, PropertyGroup } from '@shared/definitions/property-group';
import { withAddedProperties } from '@shared/definitions/group-members';
import { AuthStore } from '@core/services/auth/auth.store';
import { User } from '@shared/definitions/user';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { PropertyService } from '@core/services/data/properties.service';
import { BuildingCharacteristicsService } from '@core/services/property-analysis/building-characteristics.service';
import { isPermissionDeniedError } from '@core/services/auth/auth-transition-error';

@Injectable({
  providedIn: 'root'
})
export class PropertyGroupService implements OnDestroy {
  propertyGroups = new BehaviorSubject<PropertyGroup[]>([]);
  private user: User | null = null;

  private propertyGroupsData: PropertyGroup[] = [];
  private subsPropertyGroups: Subscription = new Subscription();

  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);

  /** Stable change-key for deduping auth effect runs. */
  private readonly authKey = computed(() => {
    const u = this.authStore.user();
    return `${u?.userUID ?? ''}:${u?.email ?? ''}`;
  });

  constructor(
    private firestore: Firestore,
    private pathService: FirestorePathService,
    private workspaceStore: WorkspaceStore,
    private injector: Injector
  ) {
    this.initializeService();
  }
  
  /**
   * Lazy load services to avoid circular dependency issues
   */
  private async getPropertyService(): Promise<PropertyService> {
    const { PropertyService } = await import('@core/services/data/properties.service');
    return this.injector.get(PropertyService);
  }

  private async getBuildingCharacteristicsService(): Promise<BuildingCharacteristicsService> {
    const { BuildingCharacteristicsService } = await import('@core/services/property-analysis/building-characteristics.service');
    return this.injector.get(BuildingCharacteristicsService);
  }

  private initializeService(): void {
    // Gate on BOTH authenticated user AND claimsReady: fresh signups
    // momentarily emit a user whose token lacks workspace custom claims,
    // and workspace-scoped Firestore listeners reject those. See
    // `BaseCrudService.initializeService` for the same pattern.
    effect(async () => {
      const _key = this.authKey();
      const claimsReady = this.authStore.claimsReady();
      const user = this.authStore.user();

      this.user = user;
      if (!user || !user.userUID) {
        this.clearPropertyGroups();
        return;
      }
      if (!claimsReady) return;

      try {
        await this.workspaceStore.waitForWorkspace(10000);
        this.fetchAllPropertyGroups();
      } catch (error) {
        console.error(`[workspace] ❌ property-groups: Workspace timeout, skipping data fetch`, error);
      }
    });
  }

  ngOnDestroy(): void {
    this.subsPropertyGroups.unsubscribe();
  }

  /**
   * Get the property groups collection reference for current user
   */
  private getPropertyGroupsCollection() {
    if (!this.user?.userUID) {
      throw new Error('User not authenticated');
    }
    const path = this.pathService.getWorkspaceCollection('property_groups');
    return collection(this.firestore, path);
  }

  /**
   * Get a specific property group document reference
   */
  private getPropertyGroupDocument(groupUID: string) {
    if (!this.user?.userUID) {
      throw new Error('User not authenticated');
    }
    const path = this.pathService.getWorkspaceDocument('property_groups', groupUID);
    return doc(this.firestore, path);
  }

  private fetchAllPropertyGroups(): void {
    if (!this.user?.userUID) return;

    // Unsubscribe from previous subscription if it exists
    if (this.subsPropertyGroups) {
      this.subsPropertyGroups.unsubscribe();
    }

    try {
      const propertyGroupsRef = this.getPropertyGroupsCollection();
      
      this.subsPropertyGroups = collectionData(propertyGroupsRef, { idField: 'groupUID' }).pipe(
        map(groups => groups.map(group => new PropertyGroup(group as Partial<PropertyGroup>))),
        catchError(this.handleError),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: groups => {
          // ALWAYS update - ignore loading coordinators and other blocking mechanisms
          this.propertyGroupsData = groups;
          this.propertyGroups.next(this.propertyGroupsData);
        },
        error: error => {
          this.handleError(error);
        }
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  getPropertyGroupsByUser(): Observable<PropertyGroup[]> {
    return this.propertyGroups.pipe(
      map(groups => groups.filter(group => 
        group.deleted !== true
      ))
    );
  }

  getPropertyGroupById(groupUID: string, forceFirestoreFetch = false): Observable<PropertyGroup | null> {
    // If force fetch is requested, go directly to Firestore
    if (forceFirestoreFetch) {
      return this.getPropertyGroupByIdFromFirestore(groupUID);
    }
    
    // Otherwise, try to get from cache first
    return this.propertyGroups.pipe(
      map(groups => {
        const group = groups.find(group => group.groupUID === groupUID);
        // Return null if group is deleted or not found
        if (group && !group.deleted) {
          return group;
        }
        
        // If not found in cache, try direct Firestore fetch
        return null;
      }),
      switchMap(group => {
        if (group) {
          return of(group);
        }
        
        // Fallback to direct Firestore fetch
        return this.getPropertyGroupByIdFromFirestore(groupUID);
      })
    );
  }
  
  // Direct fetch from Firestore (bypassing the cache)
  getPropertyGroupByIdFromFirestore(groupUID: string): Observable<PropertyGroup | null> {
    if (!this.user?.userUID) {
      return throwError(() => new Error('User not authenticated'));
    }

    
    try {
      const groupDocRef = this.getPropertyGroupDocument(groupUID);

      return docData(groupDocRef, { idField: 'groupUID' }).pipe(
        map(data => {
          if (!data || (data as { deleted?: boolean }).deleted === true) {
            return null;
          }

          const group = new PropertyGroup(data as Partial<PropertyGroup>);
          
          // Add to local cache if not already there
          const index = this.propertyGroupsData.findIndex(g => g.groupUID === groupUID);
          if (index === -1) {
            this.propertyGroupsData.push(group);
            this.propertyGroups.next([...this.propertyGroupsData]);
          } else {
            // Update existing group in cache
            this.propertyGroupsData[index] = group;
            this.propertyGroups.next([...this.propertyGroupsData]);
          }
          
          return group;
        }),
        catchError(error => {
          console.error(`Error fetching group ${groupUID} from Firestore:`, error);
          return of(null);
        })
      );
    } catch (error) {
      console.error(`Error getting document reference for group ${groupUID}:`, error);
      return of(null);
    }
  }

  // Helper method to parse and validate date values
  private parseDate(dateValue: unknown): Date | null {
    if (!dateValue) return null;

    // Handle Firestore Timestamp objects
    if (typeof dateValue === 'object' && dateValue !== null && 'toDate' in dateValue && typeof (dateValue as { toDate?: unknown }).toDate === 'function') {
      return (dateValue as { toDate: () => Date }).toDate();
    }

    // Handle string dates or already converted Date objects
    const parsedDate = dateValue instanceof Date ? dateValue : new Date(dateValue as string | number);

    // Validate the date is not Invalid Date
    return !isNaN(parsedDate.getTime()) ? parsedDate : null;
  }

  addPropertyGroup(group: Omit<PropertyGroup, 'groupUID'>): Observable<string> {
    if (!this.user?.userUID) {
      return throwError(() => new Error('User not authenticated'));
    }

    // Deep clone the group to avoid modifying the original
    const groupToSave = JSON.parse(JSON.stringify({
      ...group,
      createdAt: new Date(),
      updatedAt: new Date()
    }));

    // Ensure date fields are properly formatted before saving
    if (groupToSave.members && Array.isArray(groupToSave.members)) {
      groupToSave.members = groupToSave.members.map((member: GroupMember) => {
        if (member.addedAt) {
          const parsedDate = this.parseDate(member.addedAt);
          member.addedAt = parsedDate || new Date(); // Use current date as fallback
        }
        return member;
      });
    }

    try {
      const propertyGroupsRef = this.getPropertyGroupsCollection();
      
      return from(addDoc(propertyGroupsRef, groupToSave)).pipe(
        map(docRef => docRef.id),
        tap(groupUID => {
          // SIMPLE OPTIMISTIC UPDATE: Add to UI immediately
          const newGroup = new PropertyGroup({ ...groupToSave, groupUID });
          this.propertyGroupsData.push(newGroup);
          this.propertyGroups.next(this.propertyGroupsData);
        }),
        catchError(this.handleError)
      );
    } catch (error) {
      return throwError(() => error);
    }
  }

  deletePropertyGroup(groupUID: string): Observable<void> {
    if (!this.user?.userUID) {
      return throwError(() => new Error('User not authenticated'));
    }

    // SIMPLE OPTIMISTIC UPDATE: Remove from UI immediately
    this.propertyGroupsData = this.propertyGroupsData.filter(g => g.groupUID !== groupUID);
    this.propertyGroups.next(this.propertyGroupsData);

    try {
      const groupDocRef = this.getPropertyGroupDocument(groupUID);
      
      return from(deleteDoc(groupDocRef)).pipe(
        catchError(error => {
          // On error: reload all data to restore correct state
          this.fetchAllPropertyGroups();
          return this.handleError(error);
        })
      );
    } catch (error) {
      // On error: reload all data to restore correct state
      this.fetchAllPropertyGroups();
      return throwError(() => error);
    }
  }

  updatePropertyGroup(groupUID: string, groupData: Partial<PropertyGroup>): Observable<void> {
    if (!this.user?.userUID) {
      return throwError(() => new Error('User not authenticated'));
    }

    // Deep clone the data to avoid modifying the original
    const updatedData = JSON.parse(JSON.stringify({
      ...groupData,
      updatedAt: new Date()
    }));

    // Ensure date fields are properly formatted before saving
    if (updatedData.members && Array.isArray(updatedData.members)) {
      updatedData.members = updatedData.members.map((member: GroupMember) => {
        if (member.addedAt) {
          const parsedDate = this.parseDate(member.addedAt);
          member.addedAt = parsedDate || new Date(); // Use current date as fallback
        }
        return member;
      });
    }

    try {
      const groupDocRef = this.getPropertyGroupDocument(groupUID);

      return from(updateDoc(groupDocRef, updatedData)).pipe(
        tap(() => {
          const index = this.propertyGroupsData.findIndex(g => g.groupUID === groupUID);
          if (index !== -1) {
            this.propertyGroupsData[index] = new PropertyGroup({
              ...this.propertyGroupsData[index],
              ...updatedData
            });
            this.propertyGroups.next([...this.propertyGroupsData]);
          }
        }),
        // User-initiated WRITE: surface ALL errors (including permission-denied)
        // so callers can keep their dialog open and show feedback. The shared
        // handleError swallows permission-denied into EMPTY — correct for
        // background read listeners during a workspace switch, but here it
        // masqueraded a failed save as a success (the promise resolved void).
        catchError((error) => throwError(() => error))
      );
    } catch (error) {
      return throwError(() => error);
    }
  }

  // Get count of property groups
  getPropertyGroupCount(): Promise<number> {
    return Promise.resolve(
      this.propertyGroupsData.filter(g => !g.deleted).length
    );
  }

  // Add method for soft deletion
  softDeletePropertyGroup(groupUID: string): Observable<void> {
    if (!this.user?.userUID) {
      return throwError(() => new Error('User not authenticated'));
    }

    try {
      const groupDocRef = this.getPropertyGroupDocument(groupUID);
      
      // Set deleted flag and deletedAt timestamp
      const updateData = {
        deleted: true,
        deletedAt: new Date()
      };

      return from(updateDoc(groupDocRef, updateData)).pipe(
        tap(() => {
          // Update the local data
          const index = this.propertyGroupsData.findIndex(g => g.groupUID === groupUID);
          if (index !== -1) {
            this.propertyGroupsData[index].deleted = true;
            this.propertyGroupsData[index].deletedAt = new Date();
            this.propertyGroups.next([...this.propertyGroupsData]);
          }
        }),
        catchError(this.handleError)
      );
    } catch (error) {
      return throwError(() => error);
    }
  }

  // Helper method to restore a soft-deleted property group
  restorePropertyGroup(groupUID: string): Observable<void> {
    if (!this.user?.userUID) {
      return throwError(() => new Error('User not authenticated'));
    }

    try {
      const groupDocRef = this.getPropertyGroupDocument(groupUID);
      
      // Clear deleted flag and deletedAt timestamp
      const updateData = {
        deleted: false,
        deletedAt: null
      };

      return from(updateDoc(groupDocRef, updateData)).pipe(
        tap(() => {
          // Update the local data
          const index = this.propertyGroupsData.findIndex(g => g.groupUID === groupUID);
          if (index !== -1) {
            this.propertyGroupsData[index].deleted = false;
            this.propertyGroupsData[index].deletedAt = null;
            this.propertyGroups.next([...this.propertyGroupsData]);
          }
        }),
        catchError(this.handleError)
      );
    } catch (error) {
      return throwError(() => error);
    }
  }

  // Add a property or another group to a property group
  addMemberToGroup(groupUID: string, memberId: string, memberType: 'property' | 'group'): Observable<void> {
    if (!this.user?.userUID) {
      return throwError(() => new Error('User not authenticated'));
    }

    // Find the group
    const group = this.propertyGroupsData.find(g => g.groupUID === groupUID);
    if (!group) {
      return throwError(() => new Error('Property group not found'));
    }

    // Check if the member already exists
    const memberExists = group.members.some(member => member.id === memberId && member.type === memberType);
    if (memberExists) {
      return throwError(() => new Error('Member already exists in this group'));
    }

    // Create the new member
    const newMember = {
      id: memberId,
      type: memberType,
      addedAt: new Date()
    };

    // Add to the group's members array
    const updatedMembers = [...group.members, newMember];

    // Update the group first
    return this.updatePropertyGroup(groupUID, { members: updatedMembers }).pipe(
      switchMap(() => {
        // If adding a property to a building, apply building characteristic inheritance
        if (memberType === 'property') {
          return from(this.applyBuildingInheritance(memberId, groupUID));
        }
        return of(void 0);
      })
    );
  }
  
  /**
   * Apply building characteristic inheritance to a property
   * Called when a property joins a building
   */
  private async applyBuildingInheritance(propertyId: string, buildingId: string): Promise<void> {
    try {
      const [propertyService, buildingCharacteristicsService] = await Promise.all([
        this.getPropertyService(),
        this.getBuildingCharacteristicsService()
      ]);
      
      // Get the property
      const property = await firstValueFrom(propertyService.getPropertyById(propertyId));
      
      if (property) {
        // Apply inherited characteristics
        await buildingCharacteristicsService.applyInheritedCharacteristics(property, buildingId);
      }
    } catch (error) {
      console.error('Error applying building inheritance:', error);
      // Don't throw - we don't want to fail the whole operation if inheritance fails
    }
  }

  // Remove a member from a property group
  removeMemberFromGroup(groupUID: string, memberId: string): Observable<void> {
    if (!this.user?.userUID) {
      return throwError(() => new Error('User not authenticated'));
    }

    // Find the group
    const group = this.propertyGroupsData.find(g => g.groupUID === groupUID);
    if (!group) {
      return throwError(() => new Error('Property group not found'));
    }

    // Find the member to check its type
    const member = group.members.find(m => m.id === memberId);
    if (!member) {
      return throwError(() => new Error('Member not found in this group'));
    }

    // Filter out the member
    const updatedMembers = group.members.filter(m => m.id !== memberId);

    // Update the group first, then remove inheritance if it's a property
    return this.updatePropertyGroup(groupUID, { members: updatedMembers }).pipe(
      switchMap(() => {
        // If removing a property from a building, remove building characteristic inheritance
        if (member.type === 'property') {
          return from(this.removeBuildingInheritance(memberId));
        }
        return of(void 0);
      })
    );
  }
  
  /**
   * Remove building characteristic inheritance from a property
   * Called when a property leaves a building
   */
  private async removeBuildingInheritance(propertyId: string): Promise<void> {
    try {
      const [propertyService, buildingCharacteristicsService] = await Promise.all([
        this.getPropertyService(),
        this.getBuildingCharacteristicsService()
      ]);
      
      // Get the property
      const property = await firstValueFrom(propertyService.getPropertyById(propertyId));
      
      if (property) {
        // Remove inherited characteristics and restore unit characteristics
        await buildingCharacteristicsService.removeInheritedCharacteristics(property);
      }
    } catch (error) {
      console.error('Error removing building inheritance:', error);
      // Don't throw - we don't want to fail the whole operation if inheritance removal fails
    }
  }

  // Add a property to a property group - convenience method for drag and drop
  addPropertyToGroup(propertyId: string, groupId: string): Observable<void> {
    return this.addMemberToGroup(groupId, propertyId, 'property');
  }

  /**
   * Ajoute plusieurs biens à un immeuble en une seule écriture. L'héritage des
   * caractéristiques de l'immeuble part ensuite en tâche de fond : il ne doit pas
   * retenir le dialogue (il attend un flux qui peut ne jamais émettre).
   */
  addPropertiesToGroup(propertyIds: readonly string[], groupId: string): Observable<number> {
    if (!this.user?.userUID) {
      return throwError(() => new Error('User not authenticated'));
    }
    const group = this.propertyGroupsData.find(g => g.groupUID === groupId);
    if (!group) {
      return throwError(() => new Error('Property group not found'));
    }
    const { members, added } = withAddedProperties(group.members, propertyIds);
    if (added.length === 0) return of(0);
    return this.updatePropertyGroup(groupId, { members }).pipe(
      tap(() => added.forEach(id => void this.applyBuildingInheritance(id, groupId))),
      map(() => added.length)
    );
  }

  // Refresh property groups data from the database
  refreshPropertyGroups(): void {
    // Simply call fetchAllPropertyGroups which has all the proper logic
    this.fetchAllPropertyGroups();
  }

  private clearPropertyGroups(): void {
    // Tear down the active Firestore listener BEFORE wiping local state.
    // Without this, on logout the listener fires one last permission-denied
    // before unsubscribe propagates — polluting the console and leaking a
    // race during logout/login of a different account.
    this.subsPropertyGroups.unsubscribe();
    this.subsPropertyGroups = new Subscription();
    this.propertyGroupsData = [];
    this.propertyGroups.next([]);
  }

  private handleError = (error: unknown) => {
    // Silence permission-denied unconditionally — see BaseCrudService.handleError.
    if (isPermissionDeniedError(error)) {
      return EMPTY;
    }
    console.error('An error occurred', error);
    const message = (error instanceof Error) ? error.message : 'Database error';
    return throwError(() => new Error(message));
  };
} 